import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { btn, input, Field } from "@/components/kit";
import { toast } from "sonner";
import {
  Plus, Trash2, GripVertical, ArrowLeft, Save, Loader2,
  Upload, FileText, PenLine, CheckSquare, Info, ChevronDown, Calendar, Hash, AlignLeft, Files, Scale, Lock, CreditCard, GitBranch, Globe, Zap,
} from "lucide-react";

const TASK_TYPES = [
  { v: "document", l: "Document upload", icon: Upload },
  { v: "multi_file", l: "Multiple files", icon: Files },
  { v: "form", l: "Short text", icon: FileText },
  { v: "long_text", l: "Long text", icon: AlignLeft },
  { v: "dropdown", l: "Dropdown", icon: ChevronDown },
  { v: "date", l: "Date", icon: Calendar },
  { v: "number", l: "Number", icon: Hash },
  { v: "signature", l: "E-signature", icon: PenLine },
  { v: "legal", l: "Legal document", icon: Scale },
  { v: "payment", l: "Payment / deposit", icon: CreditCard },
  { v: "checkbox", l: "Checkbox / confirm", icon: CheckSquare },
  { v: "info", l: "Info / acknowledge", icon: Info },
];
const uid = () => Math.random().toString(36).slice(2, 10);
const tField = "border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary";

function TaskRow({ task, allTasks, onChange, onDelete }) {
  const controls = useDragControls();
  const others = allTasks.filter((t) => t.id !== task.id && t.title.trim());
  const cond = task.condition || {};
  const setCond = (patch) => {
    const next = { ...cond, ...patch };
    onChange({ condition: next.task_id ? next : null });
  };
  return (
    <Reorder.Item value={task} dragListener={false} dragControls={controls} className="border border-border bg-card p-3" data-testid={`builder-task-${task.id}`}>
      <div className="flex gap-2 items-start">
        <button type="button" onPointerDown={(e) => controls.start(e)} className="mt-2 cursor-grab active:cursor-grabbing text-muted-foreground" title="Drag to reorder"><GripVertical className="h-4 w-4" /></button>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <input value={task.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Task title" data-testid={`builder-task-title-${task.id}`} className={`flex-1 ${tField}`} />
            <select value={task.type} onChange={(e) => onChange({ type: e.target.value })} data-testid={`builder-task-type-${task.id}`} className={tField}>
              {TASK_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <button type="button" onClick={onDelete} className="p-2 hover:bg-secondary text-destructive" data-testid={`builder-del-task-${task.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <input value={task.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Help text (optional)" className={`w-full ${tField} py-1.5 text-xs`} />
          {task.type === "dropdown" && (
            <textarea value={(task.options || []).join("\n")} onChange={(e) => onChange({ options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              placeholder="One option per line" rows={3} data-testid={`builder-task-options-${task.id}`} className={`w-full ${tField} text-xs`} />
          )}
          {task.type === "payment" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Amount (GBP)</span>
              <input type="number" min="0" step="0.01" value={task.amount || ""} onChange={(e) => onChange({ amount: parseFloat(e.target.value) || 0 })}
                placeholder="199.00" data-testid={`builder-task-amount-${task.id}`} className={`w-32 ${tField} text-xs`} />
            </div>
          )}
          {task.type === "legal" && (
            <div>
              <textarea value={task.template_body || ""} onChange={(e) => onChange({ template_body: e.target.value })}
                placeholder="Paste your agreement text…" rows={5} data-testid={`builder-task-template-${task.id}`} className={`w-full ${tField} text-xs font-mono`} />
              <p className="text-[11px] text-muted-foreground mt-1">Variables: {"{{client.name}}"}, {"{{client.company}}"}, {"{{client.email}}"}, {"{{org.name}}"}, {"{{date}}"}</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={task.required} onChange={(e) => onChange({ required: e.target.checked })} data-testid={`builder-task-required-${task.id}`} /> Required
            </label>
            <span className="flex items-center gap-1 text-xs text-muted-foreground ml-2"><GitBranch className="h-3.5 w-3.5" /> Show only if</span>
            <select value={cond.task_id || ""} onChange={(e) => setCond({ task_id: e.target.value })} data-testid={`builder-task-cond-field-${task.id}`} className={`${tField} text-xs`}>
              <option value="">Always</option>
              {others.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            {cond.task_id && (
              <input value={cond.equals || ""} onChange={(e) => setCond({ equals: e.target.value })} placeholder="equals value…" data-testid={`builder-task-cond-value-${task.id}`} className={`w-36 ${tField} text-xs`} />
            )}
          </div>
        </div>
      </div>
    </Reorder.Item>
  );
}

function StageCard({ stage, allTasks, onChange, onDelete, canDelete }) {
  const controls = useDragControls();
  const setTask = (tid, patch) => onChange({ tasks: stage.tasks.map((t) => (t.id === tid ? { ...t, ...patch } : t)) });
  const delTask = (tid) => onChange({ tasks: stage.tasks.filter((t) => t.id !== tid) });
  const addTask = () => onChange({ tasks: [...stage.tasks, { id: uid(), title: "", type: "document", required: true, description: "", options: [], template_body: "", amount: 0, condition: null }] });

  return (
    <Reorder.Item value={stage} dragListener={false} dragControls={controls} className="bg-card border border-border" data-testid={`builder-stage-${stage.id}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/40">
        <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-muted-foreground"><GripVertical className="h-4 w-4" /></button>
        <input value={stage.name} onChange={(e) => onChange({ name: e.target.value })} data-testid={`builder-stage-name-${stage.id}`} className="flex-1 bg-transparent font-semibold focus:outline-none" placeholder="Stage name" />
        <span className="text-xs text-muted-foreground">{stage.tasks.length} tasks</span>
        {canDelete && <button type="button" onClick={onDelete} className="p-1 hover:bg-background text-destructive" data-testid={`builder-del-stage-${stage.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="p-4 space-y-3">
        <Reorder.Group axis="y" values={stage.tasks} onReorder={(tasks) => onChange({ tasks })} className="space-y-3">
          {stage.tasks.map((t) => (
            <TaskRow key={t.id} task={t} allTasks={allTasks} onChange={(patch) => setTask(t.id, patch)} onDelete={() => delTask(t.id)} />
          ))}
        </Reorder.Group>
        <button type="button" onClick={addTask} className="text-sm text-accent font-medium inline-flex items-center gap-1 hover:underline" data-testid={`builder-add-task-${stage.id}`}><Plus className="h-4 w-4" /> Add task</button>
      </div>
    </Reorder.Item>
  );
}

export default function FlowBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = id && id !== "new";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [gated, setGated] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [category, setCategory] = useState("");
  const [stages, setStages] = useState([{ id: uid(), name: "Stage 1", tasks: [] }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get(`/flows/${id}`).then((r) => {
        setName(r.data.name); setDescription(r.data.description || ""); setGated(r.data.gated !== false); setIsPublic(!!r.data.public); setAutoApprove(!!r.data.auto_approve); setCategory(r.data.category || "");
        setStages(r.data.stages?.length ? r.data.stages : [{ id: uid(), name: "Stage 1", tasks: [] }]);
      }).catch((e) => { toast.error(apiError(e)); nav("/app/flows"); });
    }
  }, [editing, id, nav]);

  const setStage = (sid, patch) => setStages((prev) => prev.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const delStage = (sid) => setStages((prev) => prev.filter((s) => s.id !== sid));
  const addStage = () => setStages((prev) => [...prev, { id: uid(), name: `Stage ${prev.length + 1}`, tasks: [] }]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give your flow a name"); return; }
    if (stages.some((s) => s.tasks.some((t) => !t.title.trim()))) { toast.error("Every task needs a title"); return; }
    setLoading(true);
    try {
      const payload = { name, description, gated, public: isPublic, auto_approve: autoApprove, category, stages };
      if (editing) await api.put(`/flows/${id}`, payload);
      else await api.post("/flows", payload);
      toast.success("Flow saved");
      nav("/app/flows");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl">
      <button onClick={() => nav("/app/flows")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="builder-back"><ArrowLeft className="h-4 w-4" /> Back to flows</button>

      <div className="bg-card border border-border p-6 mb-6 space-y-4">
        <Field label="Flow name"><input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Landlord Onboarding" data-testid="builder-name" /></Field>
        <Field label="Description (optional)"><input className={input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this onboarding for?" data-testid="builder-description" /></Field>
        <label className="flex items-start gap-3 cursor-pointer border border-border p-3">
          <input type="checkbox" checked={gated} onChange={(e) => setGated(e.target.checked)} className="mt-1" data-testid="builder-gated-toggle" />
          <span className="flex items-center gap-2 text-sm"><Lock className="h-4 w-4 text-accent" /><span><span className="font-medium">Sequential gate</span> — lock each stage until the previous stage's required tasks are done.</span></span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer border border-border p-3">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="mt-1" data-testid="builder-public-toggle" />
          <span className="flex items-center gap-2 text-sm"><Globe className="h-4 w-4 text-accent" /><span><span className="font-medium">Show on public hub</span> — let people self-start this form from your business hub page.</span></span>
        </label>
        {isPublic && (
          <>
            <label className="flex items-start gap-3 cursor-pointer border border-border p-3" data-testid="builder-auto-approve-row">
              <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} className="mt-1" data-testid="builder-auto-approve-toggle" />
              <span className="flex items-center gap-2 text-sm"><Zap className="h-4 w-4 text-accent" /><span><span className="font-medium">Skip approval</span> — trusted clients start onboarding instantly, without waiting for you to approve them.</span></span>
            </label>
            <Field label="Hub section (optional)" hint="Group this form under a heading on your public hub, e.g. “New landlords” or “Contractors”.">
              <input className={input} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. New landlords" data-testid="builder-category" />
            </Field>
          </>
        )}
      </div>

      <Reorder.Group axis="y" values={stages} onReorder={setStages} className="space-y-4">
        {stages.map((s) => (
          <StageCard key={s.id} stage={s} allTasks={stages.flatMap((x) => x.tasks)} canDelete={stages.length > 1} onChange={(patch) => setStage(s.id, patch)} onDelete={() => delStage(s.id)} />
        ))}
      </Reorder.Group>

      <button onClick={addStage} className={`${btn.outline} mt-4`} data-testid="builder-add-stage"><Plus className="h-4 w-4" /> Add stage</button>

      <div className="sticky bottom-0 mt-8 -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 bg-background/90 backdrop-blur border-t border-border flex justify-end gap-3">
        <button onClick={() => nav("/app/flows")} className={btn.outline}>Cancel</button>
        <button onClick={save} disabled={loading} className={btn.primary} data-testid="builder-save">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save flow
        </button>
      </div>
    </div>
  );
}
